// KazakhstanMap.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

interface Region {
  id: string;
  name: string;
  data?: any; // Дополнительные данные для региона
}

interface KazakhstanMapProps {
  width?: string | number;
  height?: string | number;
  defaultFill?: string;
  hoverFill?: string;
  activeFill?: string;
  strokeWidth?: number;
  strokeColor?: string;
  regions?: Region[];
  onRegionClick?: (region: Region) => void;
  onRegionHover?: (region: Region | null) => void;
}

const KazakhstanMap: React.FC<KazakhstanMapProps> = ({
                                                       width = '100%',
                                                       height = 'auto',
                                                       defaultFill = '#5764f7',
                                                       hoverFill = '#4254f5',
                                                       activeFill = '#3244d0',
                                                       strokeWidth = 0.5,
                                                       strokeColor = '#ffffff',
                                                       regions = [],
                                                       onRegionClick,
                                                       onRegionHover
                                                     }) => {
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Загрузка SVG содержимого
  useEffect(() => {
    // Store event handlers for cleanup
    const eventHandlers: { element: Element, type: string, handler: EventListener }[] = [];

    const addEventListenerWithCleanup = (element: Element, type: string, handler: EventListener) => {
      element.addEventListener(type, handler);
      eventHandlers.push({ element, type, handler });
    };

    fetch('/kz.svg')
        .then(response => response.text())
        .then(data => {
          if (!mapContainerRef.current) return;

          // Очищаем контейнер
          mapContainerRef.current.innerHTML = '';

          // Создаем элемент-обертку
          const wrapper = document.createElement('div');
          wrapper.innerHTML = data;

          // Добавляем SVG в DOM
          const svgElement = wrapper.querySelector('svg');
          if (svgElement) {
            // Устанавливаем атрибуты SVG
            svgElement.setAttribute('width', '100%');
            svgElement.setAttribute('height', '100%');

            // Получаем все path элементы и настраиваем их
            const paths = svgElement.querySelectorAll('path');
            paths.forEach(path => {
              const regionId = path.getAttribute('id');
              if (!regionId) return;

              // Найти регион в переданном списке
              const regionData = regions.find(r => r.id === regionId);

              // Добавить атрибуты данных и название для подсказки
              if (regionData) {
                path.setAttribute('data-region-name', regionData.name);
                path.setAttribute('data-region-id', regionData.id);
              } else {
                // Add default data attributes even if region not in props
                path.setAttribute('data-region-id', regionId);
                path.setAttribute('data-region-name', regionId);
              }

              // Настройка стилей
              path.style.fill = defaultFill;
              path.style.stroke = strokeColor;
              path.style.strokeWidth = `${strokeWidth}`;
              path.style.transition = 'fill 0.3s ease';
              path.style.cursor = 'pointer';

              // Добавление обработчиков событий
              const handleMouseEnter = (e: Event) => {
                const target = e.target as SVGPathElement;
                const regionId = target.getAttribute('id');
                if (!regionId) return;

                target.style.fill = activeRegion === regionId ? activeFill : hoverFill;
                setHoveredRegion(regionId);

                // Set tooltip content and position
                const regionData = regions.find(r => r.id === regionId);
                const mouseEvent = e as MouseEvent;

                // Use region name from data or fallback to ID if region not found in props
                const tooltipText = regionData ? regionData.name : regionId;
                setTooltipContent(tooltipText);

                // Calculate position relative to the document
                const rect = mapContainerRef.current?.getBoundingClientRect();
                if (rect) {
                  // Ensure tooltip stays within viewport
                  const x = Math.min(mouseEvent.clientX, window.innerWidth - 150);
                  const y = Math.min(mouseEvent.clientY, window.innerHeight - 50);
                  setTooltipPosition({ x, y });
                } else {
                  setTooltipPosition({ x: mouseEvent.clientX, y: mouseEvent.clientY });
                }

                setTooltipOpen(true);

                if (onRegionHover) {
                  const region = regions.find(r => r.id === regionId);
                  if (region) onRegionHover(region);
                }
              };

              const handleMouseLeave = (e: Event) => {
                const target = e.target as SVGPathElement;
                const regionId = target.getAttribute('id');
                if (!regionId) return;

                target.style.fill = activeRegion === regionId ? activeFill : defaultFill;
                setHoveredRegion(null);

                // Hide tooltip
                setTooltipOpen(false);

                if (onRegionHover) {
                  onRegionHover(null);
                }
              };

              const handleClick = (e: Event) => {
                const target = e.target as SVGPathElement;
                const regionId = target.getAttribute('id');
                if (!regionId) return;

                setActiveRegion(prevActive => {
                  // Если уже активен - деактивируем
                  if (prevActive === regionId) {
                    paths.forEach(p => {
                      if (p.getAttribute('id') === prevActive) {
                        p.style.fill = hoveredRegion === prevActive ? hoverFill : defaultFill;
                      }
                    });
                    return null;
                  }

                  // Если был активен другой регион - сбрасываем его стиль
                  if (prevActive) {
                    paths.forEach(p => {
                      if (p.getAttribute('id') === prevActive) {
                        p.style.fill = defaultFill;
                      }
                    });
                  }

                  // Активируем новый регион
                  target.style.fill = activeFill;
                  return regionId;
                });

                if (onRegionClick) {
                  const region = regions.find(r => r.id === regionId);
                  if (region) onRegionClick(region);
                }
              };

              // Add event listeners with cleanup tracking
              addEventListenerWithCleanup(path, 'mouseenter', handleMouseEnter);
              addEventListenerWithCleanup(path, 'mouseleave', handleMouseLeave);
              addEventListenerWithCleanup(path, 'click', handleClick);
            });

            // Добавляем SVG в контейнер
            mapContainerRef.current.appendChild(svgElement);
          }
        })
        .catch(error => console.error('Ошибка загрузки SVG:', error));

    // Cleanup function to remove all event listeners
    return () => {
      eventHandlers.forEach(({ element, type, handler }) => {
        element.removeEventListener(type, handler);
      });
    };
  }, [regions, defaultFill, hoverFill, activeFill, strokeColor, strokeWidth, onRegionClick, onRegionHover]);

  // Обновление активного региона при изменении его состояния
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const paths = mapContainerRef.current.querySelectorAll('path');

    paths.forEach(path => {
      const regionId = path.getAttribute('id');
      if (!regionId) return;

      if (regionId === activeRegion) {
        path.style.fill = activeFill;
      } else if (regionId === hoveredRegion) {
        path.style.fill = hoverFill;
      } else {
        path.style.fill = defaultFill;
      }
    });
  }, [activeRegion, hoveredRegion, defaultFill, hoverFill, activeFill]);

  return (
      <React.Fragment>
          <Box
              ref={mapContainerRef}
              sx={{
                width,
                height,
                '& svg': { width: '100%', height: '100%' },
                '& path': { outline: 'none' },
                position: 'relative'
              }}
          />
          {tooltipOpen && (
              <div
                  style={{
                      position: 'fixed',
                      top: tooltipPosition.y + 10,
                      left: tooltipPosition.x + 10,
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      zIndex: 1000,
                      pointerEvents: 'none'
                  }}
              >
                  {tooltipContent}
              </div>
          )}
      </React.Fragment>
  );
};

export default KazakhstanMap;
